// Fix for "styled_default is not a function" https://github.com/vitejs/vite/issues/12423#issuecomment-2080351394
import '@mui/material/styles/styled';

import type { ErrorInfo } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';

import { datadogRum } from '@datadog/browser-rum';
import getQueryClient from '@montugroup/circuit-shared/utils/getQueryClient';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter } from 'react-router-dom';
import { scan as reactScan } from 'react-scan';
import App from './App';
import { ErrorFallback } from './ErrorFallback';
import { FeatureFlagProvider } from './providers/FeatureFlagProvider';
import TsrReactQueryProvider from './providers/ts-rest-react-query-provider';
import { Logger } from './utils/logger';

if (import.meta.env.VITE_REACT_SCAN === 'true') {
  reactScan({
    enabled: process.env.NODE_ENV === 'development',
    showToolbar: process.env.NODE_ENV === 'development',
    dangerouslyForceRunInProduction: false,
    log: false,
    animationSpeed: 'off',
    trackUnnecessaryRenders: process.env.NODE_ENV === 'development',
    showNotificationCount: false,
    showFPS: false,
  });
}

const queryClient = getQueryClient();

const logError = (error: Error, info: ErrorInfo) => {
  const logger = new Logger('ErrorBoundary');
  logger.error({ message: error.message, componentStack: info.componentStack });

  const renderingError = new Error(error.message);
  renderingError.name = `ErrorBoundary`;
  renderingError.stack = info.componentStack ?? undefined;
  renderingError.cause = error;

  datadogRum.addError(renderingError);
};

function AppWrapper() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={logError}>
      <QueryClientProvider client={queryClient}>
        <TsrReactQueryProvider>
          <FeatureFlagProvider>
            <BrowserRouter>
              <App />
              <ReactQueryDevtools initialIsOpen={false} />
            </BrowserRouter>
          </FeatureFlagProvider>
        </TsrReactQueryProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

const root = createRoot(document.getElementById('root')!);

root.render(<AppWrapper />);
